import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StepScreenshot } from "@/components/walkthrough/step-screenshot";
import { JourneyStatusBadge } from "./journey-status-badge";
import type { SelectedStepComparison } from "./comparison-types";

export interface ScreenshotCompareDialogProps {
  selectedStepComparison: SelectedStepComparison | null;
  onClose: () => void;
  baseRunNumber?: number;
  currentRunNumber?: number;
  activeBaseRunId: string | null;
  currentWalkthroughId: string;
}

export function ScreenshotCompareDialog({
  selectedStepComparison,
  onClose,
  baseRunNumber,
  currentRunNumber,
  activeBaseRunId,
  currentWalkthroughId,
}: ScreenshotCompareDialogProps) {
  if (!selectedStepComparison) return null;

  return (
    <Dialog
      open={Boolean(selectedStepComparison)}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="flex max-h-[94vh] w-[95vw] max-w-7xl flex-col gap-3 p-4 sm:max-w-7xl">
        <DialogHeader className="border-b pb-2">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-sm font-semibold flex items-center gap-2 flex-wrap">
              <span>
                Step #{selectedStepComparison.index} screenshot comparison
              </span>
              <JourneyStatusBadge status={selectedStepComparison.status} />
              {selectedStepComparison.route !== "—" && (
                <span className="text-xs font-mono text-muted-foreground font-normal truncate max-w-md">
                  {selectedStepComparison.route}
                </span>
              )}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Compare screenshots between baseline and current runs for step{" "}
            {selectedStepComparison.index}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0 overflow-auto p-1">
          {/* Left: Baseline Screenshot */}
          <div className="flex flex-col space-y-2 rounded-lg border border-border/80 bg-muted/20 p-3">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="font-semibold text-muted-foreground">
                Baseline (Run #{baseRunNumber})
              </span>
              <span className="text-xs text-muted-foreground">
                Step {selectedStepComparison.baselineStep?.step_number}
              </span>
            </div>
            <p className="text-xs font-medium text-foreground line-clamp-2">
              {selectedStepComparison.baselineStep?.action}
            </p>
            <div className="relative flex-1 min-h-[300px] flex items-center justify-center rounded bg-background border border-border/60 overflow-hidden">
              <StepScreenshot
                walkthroughId={activeBaseRunId}
                stepNumber={selectedStepComparison.baselineStep?.step_number}
                alt={`Baseline Step ${selectedStepComparison.baselineStep?.step_number}`}
                enableZoom={false}
                className="h-full w-full object-contain"
              />
            </div>
          </div>

          {/* Right: Current Screenshot */}
          <div className="flex flex-col space-y-2 rounded-lg border border-border/80 bg-muted/20 p-3">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="font-semibold text-foreground">
                Current (Run #{currentRunNumber})
              </span>
              <span className="text-xs text-muted-foreground">
                Step {selectedStepComparison.currentStep?.step_number}
              </span>
            </div>
            <p className="text-xs font-medium text-foreground line-clamp-2">
              {selectedStepComparison.currentStep?.action}
            </p>
            <div className="relative flex-1 min-h-[300px] flex items-center justify-center rounded bg-background border border-border/60 overflow-hidden">
              <StepScreenshot
                walkthroughId={currentWalkthroughId}
                stepNumber={selectedStepComparison.currentStep?.step_number}
                alt={`Current Step ${selectedStepComparison.currentStep?.step_number}`}
                enableZoom={false}
                className="h-full w-full object-contain"
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
